import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Folder {
  id: string;
  user_id: string;
  name: string;
  parent_id: string | null;
  color: string;
  icon: string;
  created_at: string;
  updated_at: string;
}

export interface FolderTreeNode extends Folder {
  children: FolderTreeNode[];
}

export function useFolders() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setFolders([]);
        return;
      }

      const { data, error } = await supabase
        .from("folders")
        .select("*")
        .eq("user_id", user.id)
        .order("name", { ascending: true });

      if (error) throw error;
      setFolders((data as Folder[]) || []);
    } catch (err) {
      setError(err as Error);
      console.error("Error fetching folders:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("folders-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "folders" },
        () => fetchFolders()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchFolders]);

  // Build tree structure from flat folder list
  const buildTree = useCallback((folderList: Folder[]): FolderTreeNode[] => {
    const map = new Map<string, FolderTreeNode>();
    const roots: FolderTreeNode[] = [];

    // First pass: create nodes with empty children
    folderList.forEach((folder) => {
      map.set(folder.id, { ...folder, children: [] });
    });

    // Second pass: build parent-child relationships
    folderList.forEach((folder) => {
      const node = map.get(folder.id)!;
      if (folder.parent_id && map.has(folder.parent_id)) {
        map.get(folder.parent_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort children alphabetically
    const sortChildren = (nodes: FolderTreeNode[]): FolderTreeNode[] => {
      return nodes
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((node) => ({
          ...node,
          children: sortChildren(node.children),
        }));
    };

    return sortChildren(roots);
  }, []);

  const folderTree = buildTree(folders);

  const createFolder = useCallback(
    async (name: string, parentId?: string, color?: string) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("folders")
          .insert({
            user_id: user.id,
            name: name.trim(),
            parent_id: parentId || null,
            color: color || "#6366f1",
          })
          .select()
          .single();

        if (error) throw error;
        toast.success(`Dossier "${name}" créé`);
        return data as Folder;
      } catch (err) {
        console.error("Error creating folder:", err);
        toast.error("Erreur lors de la création du dossier");
        throw err;
      }
    },
    []
  );

  const renameFolder = useCallback(async (folderId: string, newName: string) => {
    try {
      const { error } = await supabase
        .from("folders")
        .update({ name: newName.trim() })
        .eq("id", folderId);

      if (error) throw error;
      toast.success("Dossier renommé");
    } catch (err) {
      console.error("Error renaming folder:", err);
      toast.error("Erreur lors du renommage");
      throw err;
    }
  }, []);

  const deleteFolder = useCallback(async (folderId: string) => {
    try {
      const { error } = await supabase
        .from("folders")
        .delete()
        .eq("id", folderId);

      if (error) throw error;
      toast.success("Dossier supprimé");
    } catch (err) {
      console.error("Error deleting folder:", err);
      toast.error("Erreur lors de la suppression");
      throw err;
    }
  }, []);

  const moveFolder = useCallback(
    async (folderId: string, newParentId: string | null) => {
      try {
        const { error } = await supabase
          .from("folders")
          .update({ parent_id: newParentId })
          .eq("id", folderId);

        if (error) throw error;
        toast.success("Dossier déplacé");
      } catch (err) {
        console.error("Error moving folder:", err);
        toast.error("Erreur lors du déplacement");
        throw err;
      }
    },
    []
  );

  const updateFolderColor = useCallback(
    async (folderId: string, color: string) => {
      try {
        const { error } = await supabase
          .from("folders")
          .update({ color })
          .eq("id", folderId);

        if (error) throw error;
      } catch (err) {
        console.error("Error updating folder color:", err);
        throw err;
      }
    },
    []
  );

  return {
    folders,
    folderTree,
    loading,
    error,
    refetch: fetchFolders,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    updateFolderColor,
  };
}
